import { Button, Modal, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../../styles/Components/bot/bot-backtest/BacktestModal.module.css";
import { useRef } from "react";
import capitalize from "antd/es/_util/capitalize";
import { riskAppetiteColor, riskOptions } from "../../../utility/data";
import { dateString, formatCustomDate } from "../../../utility/helper";
import RequestBacktest from "./RequestBacktest";
import { FaRegFilePdf } from "react-icons/fa";
import { Link } from "react-router-dom";
import {
  PiCheckCircleBold,
  PiRobotBold,
  PiChartPieSliceBold,
  PiSwapBold,
  PiTrendUpBold,
  PiTargetBold,
  PiHandCoinsBold,
  PiMoneyBold,
  PiArrowUpRightBold,
  PiCheckSquareOffsetBold,
  PiCoinsBold,
  PiArrowDownBold,
  PiScalesBold,
  PiCalculatorBold,
  PiCalendarBold,
  PiClockBold,
  PiBrainBold,
} from "react-icons/pi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiRefreshCcw } from "react-icons/fi";
import { getLatestBacktest } from "../../../redux/bot/actions";
import useUserData from "../../../hooks/useUserData";

const statusFormatter = {
  processing: "Under Process",
  processed: "Finished",
  not_processed: "Pending",
  failed: "Failed",
};

function BacktestModal({ bot, showModal, closeModal, botData, isMiniApp }) {
  const {
    botState: { latestBacktests, latestBacktestsStatus, latestBacktestsError },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const { username, email } = useUserData();

  const refresher = useRef(false);

  const {
    status,
    risk_apetite: risk,
    exchange_id: exchnge,
    start_date: start,
    end_date: end,
    signal_mode: signalMode,
    ai_assistant_mode: aiMode,
    pdf_url: pdfUrl,
    plot_image_base64: plot,
    requested_backtest_date: requestTime,
    overall_win_rate: winRate,
    total_closed_positions: closedPositions,
    overall_accuracy: accuracy,
    total_profitable_positions: profitablePositions,
    overall_profit_percent: profitPercent,
    total_profits_usd: profitUsd,
    num_coins_traded: numCoins,
    cagr,
    max_drawdown: maxDrawdown,
    risk_reward_ratio: riskReward,
    profit_factor: profitFactor,
    expectancy,
  } = latestBacktests?.[bot] || {};

  const { bot_name: name, bot_creation_id: id } = botData;

  const isLoading =
    latestBacktestsStatus?.[bot] === "loading" &&
    (!latestBacktests?.[bot] ||
      latestBacktests?.[bot]?.status === "not_processed");
  const hasData = Boolean(
    latestBacktests?.[bot]?.status || latestBacktests?.[bot]?.pdf_url,
  );
  const backtestProcessed = latestBacktests?.[bot]?.status !== "not_processed";

  const refreshHandler = () => {
    refresher.current = true;
    dispatch(getLatestBacktest(username, email, id));
  };

  return (
    <Modal
      open={showModal}
      centered
      footer={null}
      onCancel={closeModal}
      className={styles.modal}
      styles={{
        body: { minHeight: "150px" },
        header: { marginBottom: "15px" },
      }}
      title={
        <div className={styles.modalHeader}>
          <h4>Backtest Report</h4>
          {latestBacktestsStatus?.[bot] !== "loading" && hasData && (
            <Button className={styles.refreshBtn} onClick={refreshHandler}>
              <FiRefreshCcw />
              Refresh Report
            </Button>
          )}
        </div>
      }
      width={680}
    >
      {latestBacktestsStatus?.[bot] === "loading" && refresher.current && (
        <div className={styles.overlayLoader}>
          <Spin />
          Refreshing report...
        </div>
      )}
      {isLoading && (
        <div className={styles.loader}>
          <Spin />
          <h4>Loading backtest report...</h4>
        </div>
      )}

      {hasData && !isLoading ? (
        backtestProcessed ? (
          <ul className={styles.infoList}>
            {/* === SUMMARY HEADER === */}
            <li className={styles.sectionHeader}>Bot Overview</li>

            <li>
              <span className={styles.label}>
                <PiRobotBold /> Bot Name:
              </span>
              <span className={styles.value}>{name}</span>
            </li>

            <li>
              <span className={styles.label}>
                <PiCheckCircleBold /> Status:
              </span>
              <span
                className={styles.value}
                style={status === "processed" ? { color: "#0AAF47" } : {}}
              >
                {statusFormatter?.[status]}
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiChartPieSliceBold /> Risk Appetite:
              </span>
              <span
                className={styles.value}
                style={{ color: riskAppetiteColor?.[+risk - 1] }}
              >
                {riskOptions?.[+risk - 1]}
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiSwapBold /> Exchange:
              </span>
              <span className={styles.value}>
                {exchnge === "cryptocrispy"
                  ? "Demo Account"
                  : capitalize(exchnge)}
              </span>
            </li>

            {/* === PERFORMANCE METRICS === */}
            <li className={styles.sectionHeader}>Performance</li>

            <li>
              <span className={styles.label}>
                <PiTrendUpBold /> Win Rate:
              </span>
              <span
                className={styles.value}
                style={winRate >= 50 ? { color: "#0AAF47" } : {}}
              >
                {winRate}
              </span>
              {/*<span className={styles.value}>(Risk/Reward: {riskReward})</span>*/}
            </li>

            <li>
              <span className={styles.label}>
                <PiTargetBold /> Signal Accuracy:
              </span>
              <span className={styles.value}>{accuracy}%</span>
            </li>

            <li>
              <span className={styles.label}>
                <PiHandCoinsBold /> Total Return:
              </span>
              <span
                className={styles.value}
                style={
                  profitPercent >= 0
                    ? { color: "#0AAF47" }
                    : { color: "#FF4D4F" }
                }
              >
                {profitPercent}%
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiMoneyBold /> Total Earnings:
              </span>
              <span
                className={styles.value}
                style={
                  profitUsd >= 0 ? { color: "#0AAF47" } : { color: "#FF4D4F" }
                }
              >
                {profitUsd} USD
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiArrowUpRightBold /> Annual Growth Rate (CAGR):
              </span>
              <span className={styles.value}>{cagr}%</span>
            </li>

            {/* === TRADE ACTIVITY === */}
            <li className={styles.sectionHeader}>Trade Activity</li>

            <li>
              <span className={styles.label}>
                <PiCheckSquareOffsetBold /> Winning Positions:
              </span>
              <span className={styles.value}>{profitablePositions}</span>
              <span className={styles.value}>(Closed: {closedPositions})</span>
            </li>

            <li>
              <span className={styles.label}>
                <PiCoinsBold /> Assets Traded:
              </span>
              <span className={styles.value}>{numCoins}</span>
            </li>

            {/* === RISK & EFFICIENCY === */}
            <li className={styles.sectionHeader}>Risk & Efficiency</li>

            <li>
              <span className={styles.label}>
                <PiArrowDownBold /> Max Drawdown:
              </span>
              <span
                className={styles.value}
                style={{ color: maxDrawdown > 20 ? "#FF4D4F" : "#0AAF47" }}
              >
                {maxDrawdown}%
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiScalesBold /> Gross Profit / Loss Ratio:
              </span>
              <span
                className={styles.value}
                style={profitFactor >= 1.5 ? { color: "#0AAF47" } : {}}
              >
                {profitFactor}
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiCalculatorBold /> Expected Value per Trade:
              </span>
              <span
                className={styles.value}
                style={
                  expectancy >= 0 ? { color: "#0AAF47" } : { color: "#FF4D4F" }
                }
              >
                {expectancy}
              </span>
            </li>

            {/* === TEST DETAILS === */}
            <li className={styles.sectionHeader}>Test Details</li>

            <li>
              <span className={styles.label}>
                <PiCalendarBold /> Backtest Period:
              </span>
              <span className={styles.value}>
                {dateString(start)} → {dateString(end)}
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiClockBold /> Requested On:
              </span>
              <span className={styles.value}>
                {formatCustomDate(requestTime, "yes")}
              </span>
            </li>

            <li>
              <span className={styles.label}>
                <PiBrainBold /> Signal Mode:
              </span>
              <span className={styles.value}>
                {signalMode?.replace("_", " ")} {aiMode && `(${aiMode})`}
              </span>
            </li>
          </ul>
        ) : (
          <div className={styles.emptyBox}>
            <h4>
              <span className={styles.loading}>
                <AiOutlineLoading3Quarters size={16} color="#0c4cfc" />
              </span>
              Backtest in progress…
            </h4>
            <p>
              Your request is being processed. Results will be available
              shortly.
            </p>
          </div>
        )
      ) : (
        ""
      )}

      {hasData && backtestProcessed && (
        <div className={styles.actionBtns}>
          <RequestBacktest
            className={`${styles.btn} ${styles.backtestBtn}`}
            targetBot={bot}
            closeModal={closeModal}
          />
          {!isMiniApp && (
            <Link
              className={`${styles.btn} ${styles.exportBtn}`}
              to={`/bot-automation/backtest-report?pdf=${pdfUrl}`}
              // href={pdfUrl}
              // download={`${name}_${formatCustomDate(
              //   start,
              // )}-${formatCustomDate(end)}.pdf`}
            >
              <FaRegFilePdf />
              Export to PDF
            </Link>
          )}
        </div>
      )}

      {!hasData && !isLoading && (
        <div className={styles.emptyBox}>
          <h4>No backtest data available. Please request a new backtest.</h4>
          <RequestBacktest
            className={`${styles.btn} ${styles.backtestBtn}`}
            targetBot={bot}
            closeModal={closeModal}
          />
        </div>
      )}

      {hasData && plot && (
        <div className={styles.chartBox}>
          <img
            src={`data:image/png;base64,${plot}`}
            alt={"plot image"}
            className={styles.plotImage}
          />
        </div>
      )}
    </Modal>
  );
}

export default BacktestModal;
